// Package imports:
import React, { useRef, useState } from "react";
import Bugsnag from "@bugsnag/js";
import { Form, Formik } from "formik";
import * as Yup from 'yup';
// Component imports:
import Alert from "../../../ui-elements/Alert/Alert";
import Button from "../../../ui-elements/Button/Button";
import Input from "../../../ui-elements/Input/Formik/Input";
import Link from "../../../ui-elements/Link/Link";
import LockIcon from "../../../ui-elements/LockIcon/LockIcon";
import DisplayBox from "../../DisplayBox/DisplayBox";
import Table from "../../../ui-elements/Table/Table";
import Checkbox from "../../../ui-elements/Input/Formik/Checkbox";
import ExampleReportButton from "../../ExampleReportButton/ExampleReportButton";
// Service imports:
import { GET_KELDAN_API_URL } from "../../../services/config";
import { useBuyingProcess } from "../../../services/buyhook";
import { ErrorMessages } from "../../../services/errorMessages";
// Type imports:
import { IKeldanApiResponse } from "../../../types/KeldanTypes";
import { IPropertySearchResult } from "../../../types/PropertyTypes";

interface IProps {
    isAuthenticated: boolean,
    bondsAllowed: boolean
}

interface IBuyFasteignaSkra {
    whereClick: 'form' | 'list'
    fastanr?: string,
    heitisnr?: string,
    bonds: boolean | null
    reference: string | null
}

const Fasteignaskra: React.FC<IProps> = ({
    isAuthenticated,
    bondsAllowed
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<IPropertySearchResult>();
    const [isSearchFormLoading, setIsSearchFormLoading] = useState(false);

    const searchResultsRef = useRef<HTMLDivElement | null>(null);

    const { tryPurchaseItem, tryToBuyItem, resetState, setToastError, ModalsAndToasts } = useBuyingProcess(async (values: IBuyFasteignaSkra, modalReference, modalBonds) => {
        try {
            const { fastanr, heitisnr, bonds, reference } = values;
            let url = '';
            let query = new URLSearchParams();
            if (fastanr) {
                url = `${GET_KELDAN_API_URL()}/Property/PropertySearchFastanr`;
                query = new URLSearchParams({
                    term: fastanr,
                    reference: reference ?? modalReference,
                    bonds: (bonds === null) ? `${modalBonds}` : `${bonds}`
                })
            } else if (heitisnr) {
                url = `${GET_KELDAN_API_URL()}/Property/PropertySearchHeiti`;
                query = new URLSearchParams({
                    term: heitisnr,
                    reference: reference ?? modalReference
                })
            }

            const response = await fetch(`${url}?${query.toString()}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8'
                }
            });
            // Done after the request for better user experience.
            if (heitisnr) setSearchTerm(heitisnr);
            if (response.ok) {
                if (response.redirected) {
                    window.location.href = response.url;
                    return;
                }
                const responseBody: IKeldanApiResponse<IPropertySearchResult> = await response.json();
                const { message } = responseBody;
                if (message === ErrorMessages.NoCard) {
                    resetState('no card')
                } else if (message) {
                    resetState({
                        type: 'alert',
                        headText: message
                    });
                } else {
                    resetState('bad request');
                }
            } else {
                resetState('response not ok');
            }
        } catch (e) {
            resetState('network error');
        }
    }, 'fasteignaskrá');

    const handleSearch = async (values: ISearchFormValues) => {
        try {
            const { heiti } = values;
            const requestQuery = new URLSearchParams({
                term: heiti
            });
            const url = `${GET_KELDAN_API_URL()}/Property/PropertySearchAll`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: requestQuery
            });
            // Done after the request for better user experience.
            setSearchTerm(heiti);
            if (response.ok) {
                if (response.redirected) {
                    window.location.href = response.url;
                } else {
                    const responseBody: IKeldanApiResponse<IPropertySearchResult> = await response.json();
                    const { message, result } = responseBody;
                    if (result) {
                        setSearchResults(result);
                        searchResultsRef.current?.scrollIntoView();
                    } else if (message === ErrorMessages.NoCard) {
                        setToastError('no card');
                    } else if (message) {
                        setToastError({
                            type: 'alert',
                            headText: message
                        })
                    } else {
                        setToastError('bad request');
                    }
                }
            } else {
                setToastError('response not ok');
            }
        } catch (e) {
            if (e instanceof Error) Bugsnag.notify(e);
            setToastError('network error');
        }
        // Reset all loading states.
        setIsSearchFormLoading(false)
    }

    return (
        <div className='KCL_property-search-form'>
            <div className="shell">
                <h3>
                    {!isAuthenticated && <LockIcon size='lg' />}
                    <span>Uppfletting í Fasteignaskrá Íslands</span>
                </h3>
                <Formik
                    initialValues={{
                        heiti: ''
                    }}
                    validationSchema={Yup.object({
                        heiti: Yup.string().required('Leit má ekki vera tóm')
                    })}
                    onSubmit={(values) => {
                        setIsSearchFormLoading(true);
                        handleSearch(values)
                    }}
                    component={(props: { values: ISearchFormValues }) => (
                        <SearchSubFormComponent
                            isAuthenticated={isAuthenticated}
                            loading={isSearchFormLoading}
                            values={props.values}
                        />
                    )}
                />
                <div className='search-form-description'>
                    <span>Með því að slá inn fastanúmer er hægt að kaupa skýrslu frá Fasteignaskrá. <Link linkSize="18" href="/Verdskra">Sjá verðskrá.</Link></span>
                </div>
                <Formik
                    initialValues={{
                        fastanr: '',
                        tilvisun: '',
                        bonds: false
                    }}
                    validationSchema={Yup.object({
                        fastanr: Yup.string().required('Fastanúmer má ekki vera tómt'),
                        tilvisun: Yup.string(),
                        bonds: Yup.boolean()
                    })}
                    onSubmit={({ fastanr, bonds, tilvisun }) => tryPurchaseItem({
                        fastanr,
                        bonds,
                        reference: tilvisun,
                        whereClick: 'form'
                    })}
                    component={(props: { values: IPurchasingFormValues }) => (
                        <PurchasingSubFormComponent
                            isAuthenticated={isAuthenticated}
                            bondsAllowed={bondsAllowed}
                            loading={(
                                tryToBuyItem !== null
                                && tryToBuyItem.whereClick === 'form'
                            )}
                            values={props.values}
                        />
                    )}
                />
                <ExampleReportButton size='lg' reportType='property' />
            </div>

            {searchResults !== undefined &&
                <div className="search-results section--gray" ref={searchResultsRef}>
                    <div className="shell">
                        <h2>Leitarniðurstöður fyrir {searchTerm}</h2>
                        {searchResults?.response?.addresses
                            ? <DisplayBox>
                                <Table
                                    tableSize="lg"
                                    data={searchResults.response.addresses}
                                    columns={[{
                                        title: 'Heitisnúmer',
                                        dataAccessor: ({address: {name_number}}) => name_number,
                                        textAlign: 'left'
                                    }, {
                                        title: 'Heiti',
                                        dataAccessor: ({address: {street}}) => street,
                                        textAlign: 'left'
                                    }, {
                                        title: 'Húsnúmer',
                                        dataAccessor: ({address: {house_number}}) => house_number,
                                        textAlign: 'left'
                                    }, {
                                        title: 'Sveitarfélag',
                                        dataAccessor: ({address: {county}}) => county,
                                        textAlign: 'left'
                                    }, {
                                        title: 'Byggð',
                                        dataAccessor: ({address: {city}}) => city,
                                        textAlign: 'left'
                                    }, {
                                        title: 'Yfirlitsmynd',
                                        dataAccessor: ({ address: {name_number} }) => (
                                            <Button
                                                buttonType="buy"
                                                size='sm'
                                                showLoader={(
                                                    tryToBuyItem !== null
                                                    && tryToBuyItem.heitisnr === name_number
                                                    && tryToBuyItem.whereClick === 'list'
                                                )}
                                                onClick={() => name_number && tryPurchaseItem({
                                                    heitisnr: name_number,
                                                    bonds: null,
                                                    reference: null,
                                                    whereClick: 'list'
                                                }, 'confirm-modal-with-reference')}
                                            >
                                                Kaupa
                                            </Button>
                                        )
                                    }]}
                                />
                            </DisplayBox>
                            : <Alert type='info' headText="Engar niðurstöður fundust" />
                        }
                    </div>
                </div>
            }
            <ModalsAndToasts />
        </div>
    );
}

export default Fasteignaskra;

interface ISearchFormValues {
    heiti: string
}

interface ISearchSubFormProps {
    isAuthenticated: boolean,
    values: ISearchFormValues,
    loading: boolean
}

const SearchSubFormComponent: React.FC<ISearchSubFormProps> = ({
    isAuthenticated,
    values,
    loading
}) => {
    return (
        <Form>
            <div className="form__body">
                <div className="form__section">
                    <div className="form__row">
                        <div className="form__col">
                            <Input
                                className='input-heiti'
                                placeholder="Leita eftir götuheiti, fastanúmeri eða heitisnúmeri"
                                name="heiti"
                                id="heiti"
                                value={values.heiti}
                                disabled={!isAuthenticated}
                            />
                        </div>
                        <div className="form__col actions">
                            <div className="form__actions">
                                <Button
                                    style={{'margin': '0px'}}
                                    showLoader={loading}
                                    buttonType="primary"
                                    size="lg"
                                    disabled={values.heiti === ''}
                                >
                                    Leita
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Form>
    );
}


interface IPurchasingFormValues {
    fastanr: string,
    tilvisun: string,
    bonds: boolean
}

interface IPurchasingSubFormProps {
    isAuthenticated: boolean,
    bondsAllowed: boolean,
    values: IPurchasingFormValues,
    loading: boolean
}

const PurchasingSubFormComponent: React.FC<IPurchasingSubFormProps> = ({
    isAuthenticated,
    bondsAllowed,
    values,
    loading
}) => {
    return (
        <Form>
            <div className="form__body">
                <div className="form__section">
                    <div className="form__row">
                        <div className="form__col">
                            <Input
                                className='input-fastanr'
                                label="Fastanúmer eignar"
                                name="fastanr"
                                id="fastanr"
                                value={values.fastanr}
                                disabled={!isAuthenticated}
                            />
                        </div>
                        <div className="form__col inputTilvisun">
                            <Input
                                label="Tilvísun á reikning"
                                name="tilvisun"
                                id="tilvisun"
                                value={values.tilvisun}
                                disabled={!isAuthenticated}
                            />
                        </div>
                        <div className="form__col actions">
                            <div className="form__actions">
                                <Button
                                    style={{'margin': '0px'}}
                                    showLoader={loading}
                                    buttonType="buy"
                                    size="lg"
                                    disabled={values.fastanr === ''}
                                >
                                    Kaupa
                                </Button>
                            </div>
                        </div>
                    </div>
                    {bondsAllowed &&
                        <div className="form__row">
                            <div className="form__col">
                                <Checkbox
                                    label="með veðböndum"
                                    name="bonds"
                                    id="bonds"
                                    value={values.bonds.toString()}
                                />
                            </div>
                        </div>
                    }
                </div>
            </div>
        </Form>
    );
}

